import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAdminStatus } from '../api/admin';
import { useLogout, useMe } from '../auth/session';
import { applyLanguage } from '../i18n';
import { PageErrorBoundary } from './Guards';
import { ProfileDialog } from './ProfileDialog';
import styles from './AppLayout.module.css';
import { Brand } from './Logo';

/** The platform administrator's pages (wireframes 11, 13 and 14): users, choirs and the audit log. */
export function AdminLayout() {
  const { t } = useTranslation();
  const me = useMe().data;
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const feedbackNew = useAdminStatus().data?.feedbackNew ?? 0;
  // The language saved to the account wins, as on the member pages.
  useEffect(() => {
    if (me?.locale) applyLanguage(me.locale);
  }, [me?.locale]);
  const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? `${styles.navLink} ${styles.active}` : styles.navLink);

  return (
    <>
      <header className={styles.header}>
        <div className={`${styles.brand} ${styles.adminBrand}`}>
          <Brand size={40} /> <span className={styles.adminBadge}>{t('admin.badge')}</span>
        </div>
        <nav className={styles.nav} aria-label={t('nav.main')}>
          <NavLink to="/admin/users" className={navClass}>
            {t('admin.nav.users')}
          </NavLink>
          <NavLink to="/admin/choirs" className={navClass}>
            {t('admin.nav.choirs')}
          </NavLink>
          <NavLink to="/admin/audit" className={navClass}>
            {t('admin.nav.audit')}
          </NavLink>
          <NavLink to="/admin/feedback" className={navClass}>
            {t('admin.nav.feedback')}
            {feedbackNew > 0 && (
              <span className={styles.badge} aria-label={t('admin.nav.feedbackNew', { count: feedbackNew })} data-testid="feedback-new">
                {feedbackNew}
              </span>
            )}
          </NavLink>
        </nav>
        <div className={styles.user}>
          {me && (
            <button
              type="button"
              className={styles.userName}
              data-testid="current-user"
              aria-haspopup="dialog"
              title={t('profile.open')}
              onClick={() => setProfileOpen(true)}
            >
              {t('nav.userLabel', { name: me.displayName, role: t('admin.role') })}
            </button>
          )}
          <button
            type="button"
            className={styles.logout}
            disabled={logout.isPending}
            onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })}
          >
            {t('nav.logout')}
          </button>
        </div>
      </header>
      {me && <ProfileDialog me={me} open={profileOpen} onClose={() => setProfileOpen(false)} />}
      <main>
        <PageErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </PageErrorBoundary>
      </main>
    </>
  );
}
